import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Suspense, lazy } from "react";
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation } from "react-router-dom";
import { Layout } from "./components/Layout";
import { Spinner } from "./components/ui";
import { BrowsePage } from "./pages/BrowsePage";
import { DashboardPage } from "./pages/DashboardPage";
import { ExamPage } from "./pages/ExamPage";
import { ExamResultPage } from "./pages/ExamResultPage";
import { ExamRunnerPage } from "./pages/ExamRunnerPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { LoginPage } from "./pages/LoginPage";
import { MistakesPage } from "./pages/MistakesPage";
import { QuestionDetailPage } from "./pages/QuestionDetailPage";
import { RegisterPage } from "./pages/RegisterPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { SettingsPage } from "./pages/SettingsPage";
import { StudyPage } from "./pages/StudyPage";
import { AuthProvider, useAuth } from "./state/auth";

// Recharts is by far the heaviest dependency and only the statistics page needs it, so
// it is split out of the bundle the learner downloads to start studying.
const StatsPage = lazy(() => import("./pages/StatsPage").then((module) => ({ default: module.StatsPage })));
// The topic pages pull in the markdown renderer, which nothing on the study path needs.
const TopicsPage = lazy(() => import("./pages/TopicsPage").then((module) => ({ default: module.TopicsPage })));
const TopicDetailPage = lazy(() =>
    import("./pages/TopicDetailPage").then((module) => ({ default: module.TopicDetailPage }))
);

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            // Study data changes on every answer; refetching on focus would fight the
            // optimistic session state, so the pages invalidate explicitly instead.
            refetchOnWindowFocus: false,
            retry: 1,
            staleTime: 30_000
        }
    }
});

function RequireAuth({ children }: { children: React.ReactNode }) {
    const { user, loading } = useAuth();
    const location = useLocation();

    if (loading) return <Spinner label="正在恢复登录状态" />;
    if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
    return <>{children}</>;
}

function RedirectIfAuthed({ children }: { children: React.ReactNode }) {
    const { user, loading } = useAuth();
    if (loading) return <Spinner />;
    if (user) return <Navigate to="/" replace />;
    return <>{children}</>;
}

export function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <Router>
                <AuthProvider>
                    <Routes>
                        <Route
                            path="/login"
                            element={
                                <RedirectIfAuthed>
                                    <LoginPage />
                                </RedirectIfAuthed>
                            }
                        />
                        <Route
                            path="/register"
                            element={
                                <RedirectIfAuthed>
                                    <RegisterPage />
                                </RedirectIfAuthed>
                            }
                        />

                        <Route
                            path="/forgot-password"
                            element={
                                <RedirectIfAuthed>
                                    <ForgotPasswordPage />
                                </RedirectIfAuthed>
                            }
                        />
                        {/* Not wrapped: the recovery link opens a session before this renders,
                            so RedirectIfAuthed would bounce the learner to the dashboard
                            without ever letting them set a new password. */}
                        <Route path="/reset-password" element={<ResetPasswordPage />} />

                        <Route
                            element={
                                <RequireAuth>
                                    <Layout />
                                </RequireAuth>
                            }
                        >
                            <Route index element={<DashboardPage />} />
                            <Route path="study" element={<StudyPage />} />
                            <Route path="browse" element={<BrowsePage />} />
                            <Route path="mistakes" element={<MistakesPage />} />
                            <Route
                                path="topics"
                                element={
                                    <Suspense fallback={<Spinner label="正在加载知识点" />}>
                                        <TopicsPage />
                                    </Suspense>
                                }
                            />
                            <Route
                                path="topics/:slug"
                                element={
                                    <Suspense fallback={<Spinner label="正在加载知识点" />}>
                                        <TopicDetailPage />
                                    </Suspense>
                                }
                            />
                            <Route path="questions/:number" element={<QuestionDetailPage />} />
                            <Route path="exam" element={<ExamPage />} />
                            <Route path="exam/:id" element={<ExamRunnerPage />} />
                            <Route path="exam/:id/result" element={<ExamResultPage />} />
                            <Route
                                path="stats"
                                element={
                                    <Suspense fallback={<Spinner label="正在加载图表" />}>
                                        <StatsPage />
                                    </Suspense>
                                }
                            />
                            <Route path="settings" element={<SettingsPage />} />
                        </Route>

                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </AuthProvider>
            </Router>
        </QueryClientProvider>
    );
}
